import { useState } from "owa-react";
import React from "react";
import type { LazyModule } from "owa-bundling-light";
import { LazyImport } from "owa-bundling-light";
import useLazyImport from "./utils/useLazyImport";
import canComponentAcceptRef from "./utils/canComponentAcceptRef";
import type { default as BroadComponentType, EffectiveComponentType, } from "./types/BroadComponentType";
import type { PerformanceDatapointType } from "owa-analytics-types";
import { useDeferredValueFlighted } from "owa-react-hooks/lib/useDeferredValueFlighted";

/**
 * Return component type of createLazyComponent
 *
 * Uses the common props of the lazy & placeholder component,
 * with a ref to one of the two components, depending on if the
 * inner component has been loaded yet.
 */
export type LazyComponent<TComponentProps, TPrimaryComponentRef, TPlaceholderComponentRef = TPrimaryComponentRef> = React.ForwardRefExoticComponent<React.PropsWithoutRef<React.PropsWithChildren<TComponentProps>> & React.RefAttributes<TPrimaryComponentRef | (TPlaceholderComponentRef extends void ? never : TPlaceholderComponentRef)>>;

export type ErrorHandlerFunction = (reason: any) => void;
/* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
 *	> Optional object properties should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
export type ErrorHandlerComponent = React.ComponentType<{
    error?: any;
}>;

export interface LazyComponentOptions {
    govern?: boolean;
    onlyImportIf?: () => boolean;
    addWaterfallTimings?: boolean;
}

// For usage of this API, see the owa-bundling README
function createLazyComponent<TProps, TModule, TInnerRef, TPlaceholderRef = void>(lazyModule: LazyModule<TModule>, getter: (module: TModule) => BroadComponentType<TProps, TInnerRef>, placeholder?: BroadComponentType<TProps, TPlaceholderRef>, importErrorHandler?: ErrorHandlerFunction | ErrorHandlerComponent, options?: LazyComponentOptions): LazyComponent<TProps, TInnerRef, TPlaceholderRef> {
    const lazyImport = new LazyImport(lazyModule, getter, options?.govern);
    
    /* eslint-disable-next-line owa-custom-rules/require-two-arg-forwardRef, owa-custom-rules/required-named-component-props --
     * Temporary any casting to merge react 18.3.1
     * Bypassing required-named-component-props because TProps is a type parameter for the wrapping function
     */
    return React.forwardRef(function LazyComponent(props: TProps & {
        perfDatapoint?: PerformanceDatapointType;
    }, ref: React.Ref<TInnerRef | (TPlaceholderRef extends void ? never : TPlaceholderRef)>) {
        /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
         * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
         *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
        const [error, setError] = useState<Error | undefined>(undefined);
        const ErrorView: any = typeof importErrorHandler == "object" ? importErrorHandler : undefined;
        
        const InnerComponent: BroadComponentType<TProps, TInnerRef> | undefined = useLazyImport(lazyImport, 
        /* eslint-disable-next-line owa-custom-rules/no-unused-react-set-state -- (https://aka.ms/OWALintWiki)
         * DO NOT COPY-PASTE!, add a justification or fix the issue
         *	> React setState functions from useState should only be used by direct invocation, setState(), not passed or used as a value or used in hook's array dependencies. */
        ErrorView ? setError : (importErrorHandler as ErrorHandlerFunction), options?.onlyImportIf, options?.addWaterfallTimings && props?.perfDatapoint ? props.perfDatapoint : undefined);
        
        const _InnerComponent = (InnerComponent || placeholder) as EffectiveComponentType<typeof InnerComponent>;
        
        const ComponentToRender = useDeferredValueFlighted(_InnerComponent, "fwk-deferred-lazy-component");
        
        if (ErrorView && error) {
            return <ErrorView error={error}/>;
        }
        
        if (!ComponentToRender) {
            return null;
        }
        
        if (canComponentAcceptRef(ComponentToRender)) {
            // The type here we are casting to is equivalent under all legal instantiations of
            // TProps and TRef. However, they do not typecheck to the same value in their generic
            // form.
            //
            // our hunch is that this is because React.PropsWithoutRef is declared as
            //   'ref' extends keyof P
            //      ? Pick<P, Exclude<keyof P, 'ref'>>
            //      : P;
            //
            // which is functionally equivalent for either side of the conditional type, but there is no way
            // for typescript to know that, so we have to construct the *exact* same type that typescript
            // expects before expanding generics so it will match.
            //
            // it works (and exposes unhandled issues w/ refs to TInnerRef vs TPlaceholderRef) if we change the
            // definition in node_modules to the left side of the conditional branch.
            const castProps = { ...props, ref } as React.JSX.IntrinsicAttributes & TProps & React.PropsWithoutRef<TProps> & React.RefAttributes<TInnerRef>;
            return <ComponentToRender {...castProps}/>;
        }
        else {
            return <ComponentToRender {...props}/>;
        }
    } as any) as any;
}
export default createLazyComponent;
